import { CommonModule } from '@angular/common'
import { Component, OnInit } from '@angular/core'
import { AtomModule } from '../../atom/atom.module'
import { SearchResult } from '../../gql/generated'
import { GameCardAlphaComponent } from '../../molecule/game-card-alpha/game-card-alpha.component'
import { GameCardBetaComponent } from '../../molecule/game-card-beta/game-card-beta.component'
import { GameCardGammaComponent } from '../../molecule/game-card-gamma/game-card-gamma.component'
import { NoItemsFoundComponent } from '../../molecule/no-items-found/no-items-found.component'
import { SidebarComponent } from '../../molecule/sidebar/sidebar.component'
import { SearchService } from '../../service/search.service'
import { HoverEffectDirective } from '../../utils/directives/hover-effect.directive'

interface GameRow {
  type: number
  items: SearchResult[]
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.less'],
  standalone: true,
  imports: [
    AtomModule,
    GameCardAlphaComponent,
    GameCardBetaComponent,
    GameCardGammaComponent,
    NoItemsFoundComponent,
    SidebarComponent,
    CommonModule,
    HoverEffectDirective,
  ],
})
export class HomeComponent implements OnInit {
  constructor(public searchService: SearchService) {}

  searchResults: SearchResult[] = []
  gameRows: GameRow[] = []

  componentType = GameCardBetaComponent

  // Row type item counts
  readonly rowTypeItemCount = [5, 5, 2, 3, 5, 4]

  ngOnInit(): void {
    console.log(`Home init`)
    this.searchService.search('').subscribe((results) => {
      // Get larger sample size for testing
      this.searchResults = [
        ...(results.data.search.items as SearchResult[]),
        ...(results.data.search.items as SearchResult[]),
        ...(results.data.search.items as SearchResult[]),
        ...(results.data.search.items as SearchResult[]),
        ...(results.data.search.items as SearchResult[]),
        ...(results.data.search.items as SearchResult[]),
        ...(results.data.search.items as SearchResult[]),
        ...(results.data.search.items as SearchResult[]),
        ...(results.data.search.items as SearchResult[]),
      ]

      this.organizeGameRows()
    })
  }

  organizeGameRows(): void {
    if (!this.searchResults || this.searchResults.length === 0) return

    this.gameRows = []
    const remainingResults = [...this.searchResults]
    let lastRowType: number | null = null

    while (remainingResults.length > 0) {
      // Get all available row types
      let availableTypes = Array.from(
        { length: this.rowTypeItemCount.length },
        (_, i) => i + 1
      )

      // Apply constraint: never same rowType should come one after another
      if (lastRowType !== null) {
        availableTypes = availableTypes.filter((type) => type !== lastRowType)
      }

      // Apply additional constraints:
      // 1. rowType 4, 2, and 5 cannot be adjacent
      if (lastRowType === 4 || lastRowType === 2 || lastRowType === 5) {
        availableTypes = availableTypes.filter(
          (type) => type !== 4 && type !== 2 && type !== 5
        )
      }

      // 2. rowType 5 and 6 cannot be adjacent
      if (lastRowType === 5 || lastRowType === 6) {
        availableTypes = availableTypes.filter(
          (type) => type !== 5 && type !== 6
        )
      }

      // 3. rowType 4 and 6 cannot be adjacent
      if (lastRowType === 4 || lastRowType === 6) {
        availableTypes = availableTypes.filter(
          (type) => type !== 4 && type !== 6
        )
      }

      // If no types available after constraints, use fallback options
      if (availableTypes.length === 0) {
        // Use types 1 or 3 as fallbacks since they don't have constraints
        availableTypes = [1, 3]
        // If lastRowType is 1 or 3, remove it from the fallbacks
        availableTypes = availableTypes.filter((type) => type !== lastRowType)

        // If still no available types, use any type except the last one
        if (availableTypes.length === 0) {
          availableTypes = Array.from(
            { length: this.rowTypeItemCount.length },
            (_, i) => i + 1
          ).filter((type) => type !== lastRowType)
        }
      }

      // Randomly select a type from available types
      const randomIndex = Math.floor(Math.random() * availableTypes.length)
      const rowType = availableTypes[randomIndex]

      // Get item count for this row type (1-indexed to 0-indexed)
      const itemCount = this.rowTypeItemCount[rowType - 1]
      const rowItems = remainingResults.splice(
        0,
        Math.min(itemCount, remainingResults.length)
      )

      if (rowItems.length > 0) {
        this.gameRows.push({
          type: rowType,
          items: rowItems,
        })

        // Update history of row types
        lastRowType = rowType
      }
    }
  }

  getRowClass(rowType: number): string {
    return `game-row--type-${rowType}`
  }
}
